import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

export default function CsAndItTable22({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p className="text-center text-red-600">⚠ No data available</p>;
  }

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];

  const races = data.map((d) => d.race_label);
  const employment2024 = data.map((d) => Number(d.y2024));
  const avgEmployment = data.map((d) => Number(d.avg_2018_2024));

  // ----------------- Chart Data -----------------
  const chartData = {
    labels: races,
    datasets: [
      {
        label: "Employment in 2024",
        data: employment2024,
        backgroundColor: "#2C92B8",
        borderRadius: 6,
      },
      {
        label: "Average Employment (2018–2024)",
        data: avgEmployment,
        type: "line",
        borderColor: "#B5EAD6",
        backgroundColor: "#B5EAD6",
        borderDash: [6, 5],
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Average Employment by Race (2018–2024) vs. 2024 Employment",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { callback: (v) => v.toLocaleString("en-US") },
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
      },
      y: {
        title: {
          display: true,
          text: "Race",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // ----------------- Summary Insights -----------------
  const highestAvg = data.reduce(
    (max, d) =>
      Number(d.avg_2018_2024) > max.value
        ? { race: d.race_label, value: d.avg_2018_2024 }
        : max,
    { race: data[0].race_label, value: data[0].avg_2018_2024 },
  );

  const lowestAvg = data.reduce(
    (min, d) =>
      Number(d.avg_2018_2024) < min.value
        ? { race: d.race_label, value: d.avg_2018_2024 }
        : min,
    { race: data[0].race_label, value: data[0].avg_2018_2024 },
  );

  const latestTotal = employment2024.reduce((sum, v) => sum + v, 0);

  // ----------------- PDF Export -----------------
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("cs_it_avg_emp_by_race.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center mb-6">
          Average Employment by Race (2018–2024) vs. 2024 Employment
        </h2>

        {/* Summary Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center mb-8">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Workforce (2024)</p>
            <p className="text-xl font-bold text-blue-900">
              {Number(latestTotal).toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Avg Employment</p>
            <p className="text-xl font-bold text-green-900">
              {highestAvg.race}:{" "}
              {Number(highestAvg.value).toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Lowest Avg Employment</p>
            <p className="text-xl font-bold text-red-900">
              {lowestAvg.race}:{" "}
              {Number(lowestAvg.value).toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table & Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Race</th>
                    {years.map((y) => (
                      <th
                        key={y}
                        className="p-2"
                        style={{ textAlign: "right" }}
                      >
                        {y}
                      </th>
                    ))}
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Avg (2018–2024)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100">
                      <td className="font-semibold p-2 text-left">
                        {row.race_label}
                      </td>
                      {years.map((y) => (
                        <td
                          key={y}
                          className="p-2"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {Number(row[`y${y}`]).toLocaleString("en-US")}
                        </td>
                      ))}
                      <td
                        className="font-bold text-red-600 p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.avg_2018_2024).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img src={pdf} className="w-[1.5rem]" alt="pdf icon" />
        </button>
      </div>
    </>
  );
}
